// Interfaces
export interface HeatmapPoint {
  location: google.maps.LatLng;
  weight?: unknown;   // a number, else 1
}

export interface HeatmapOptions {
  data: HeatmapPoint[];
  radius?: number;    // px
  opacity?: number;
}

// Variables
// Google's default heatmap gradient: transparent, green, yellow, red.
const GRADIENT: [number, string][] = [[0, 'rgba(102,255,0,0)'], [0.25, 'rgba(102,255,0,1)'], [0.5, 'rgba(255,255,0,1)'], [0.75, 'rgba(255,128,0,1)'], [1, 'rgba(255,0,0,1)']];

// Classes
// 256 colours of the gradient, indexed by the accumulated alpha.
function palette(): Uint8ClampedArray {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const context = canvas.getContext('2d')!;
  const gradient = context.createLinearGradient(0, 0, 256, 0);
  GRADIENT.forEach(([stop, color]) => gradient.addColorStop(stop, color));
  context.fillStyle = gradient;
  context.fillRect(0, 0, 256, 1);
  return context.getImageData(0, 0, 256, 1).data;
}

// Blurred disc stamped once per point.
function stamp(radius: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = radius * 2;
  const context = canvas.getContext('2d')!;
  const gradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
  gradient.addColorStop(0, 'rgba(0,0,0,1)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, radius * 2, radius * 2);
  return canvas;
}

// Replaces google.maps.visualization.HeatmapLayer (removed in Maps JS 3.65): a canvas over the visible map,
// redrawn on zoom and once the map is idle. Built on call: google.maps is only there in the browser.
export function createHeatmapOverlay({ data, radius = 10, opacity = 0.6 }: HeatmapOptions): google.maps.OverlayView {
  class HeatmapOverlay extends google.maps.OverlayView {
    private readonly canvas = document.createElement('canvas');
    private readonly disc = stamp(radius);
    private readonly colors = new Uint32Array(palette().buffer);
    // Each point's share of the strongest one: data never changes.
    private readonly alphas = (() => {
      const weights = data.map(point => Number(point.weight) || 1);
      const max = weights.reduce((top, weight) => Math.max(top, weight), 0);
      return weights.map(weight => weight / max);
    })();
    private idle?: google.maps.MapsEventListener;

    override onAdd(): void {
      Object.assign(this.canvas.style, { position: 'absolute', pointerEvents: 'none', opacity: String(opacity) });
      this.getPanes()?.overlayLayer.appendChild(this.canvas);
      this.idle = google.maps.event.addListener(this.getMap() as google.maps.Map, 'idle', () => this.draw());
    }

    override onRemove(): void {
      this.idle?.remove();
      this.canvas.remove();
    }

    override draw(): void {
      const projection = this.getProjection();
      const bounds = (this.getMap() as google.maps.Map | null)?.getBounds();
      const ne = bounds && projection?.fromLatLngToDivPixel(bounds.getNorthEast());
      const sw = bounds && projection?.fromLatLngToDivPixel(bounds.getSouthWest());
      if (!projection || !ne || !sw) return;

      // The visible area plus a radius around, so the edge points still blur in.
      const left = sw.x - radius;
      const top = ne.y - radius;
      const width = Math.max(1, Math.round(ne.x - sw.x + radius * 2));
      const height = Math.max(1, Math.round(sw.y - ne.y + radius * 2));
      Object.assign(this.canvas.style, { left: `${left}px`, top: `${top}px` });
      this.canvas.width = width;
      this.canvas.height = height;

      const context = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!context || !data.length) return;

      data.forEach((point, index) => {
        const pixel = projection.fromLatLngToDivPixel(point.location);
        const x = (pixel?.x ?? Number.NaN) - left - radius;
        const y = (pixel?.y ?? Number.NaN) - top - radius;
        // Its disc off the canvas (or no pixel): nothing to stamp.
        if (!(x > -2 * radius && x < width && y > -2 * radius && y < height)) return;
        context.globalAlpha = this.alphas[index]!;
        context.drawImage(this.disc, x, y);
      });

      // Accumulated alpha → gradient colour, a pixel at once.
      const image = context.getImageData(0, 0, width, height);
      const pixels = new Uint32Array(image.data.buffer);
      for (let i = 0; i < pixels.length; i++) {
        const alpha = image.data[i * 4 + 3]!;
        if (alpha) pixels[i] = this.colors[alpha]!;
      }
      context.putImageData(image, 0, 0);
    }
  }

  return new HeatmapOverlay();
}
